import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isPast } from 'date-fns';

import { Heading, Page } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { parseQueryString } from '@zg-rentals/string-utils';

import PropertiesActions from '../../actions/PropertiesActions';
import { useFreemiumDetails } from '../../hooks/queries/freemium';
import { useActivateListing } from '../../hooks/queries/useActivateListing';
import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import PropertyConstants from '../../constants/PropertyConstants';

import PaymentDetails from '../../components/Payment/PaymentDetails';
import SpinnerContainer from '../../components/SpinnerContainer';

import type { DatarouterError, FreemiumChargeResponseWebDto } from '@zg-rentals/rental-manager-env';

const PAYMENT_TYPE = {
  FREEMIUM: 'freemium',
};

/**
 * Handles the native mobile checkout flow for freemium
 *
 * The URL for the web view must provide the query param: paymentType
 * ex: /payToActivate?hostApp=ios&paymentType=freemium
 *
 * on payment success we will trigger a callback via triggerPaymentSuccessCallback
 */
const MobilePaymentPage = () => {
  const dispatch = useDispatch();

  const location = useLocation();

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const {
    properties: {
      currentProperty: {
        freemium,
        freemium: {
          activeProduct: { expirationTimestamp },
        },
      },
    },
    payment: {
      chargeAndActivateListing: { isPaymentValid },
    },
  } = useSelector((state) => state);

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [headingText, setHeadingText] = useState('Credit card information');

  const {
    refetch: refetchFreemiumDetails,
    data: freemiumDetailsData,
    error: freemiumError,
  } = useFreemiumDetails({
    listingAlias,
  });

  const { mutate: propertyActivate } = useActivateListing();

  const isActiveProductExpired = isPast(new Date(expirationTimestamp));

  const { search = '' } = location || {};
  const { paymentType, activate } = useMemo(() => parseQueryString(search) || {}, [search]);

  /**
   * isPaymentValid - handles paid inclusion property for post charge and activate
   * isActiveProductExpired - handles freemium property for the case when freemium product is already active and not expired
   */
  const shouldHandleValidPayment = isPaymentValid || !isActiveProductExpired;

  useEffect(() => {
    if (paymentType === PAYMENT_TYPE.FREEMIUM) {
      refetchFreemiumDetails();

      return;
    } else {
      // users should not get into this else block but putting a log here to see if mobile is sending an incorrect paymentType. Otherwise we will maye freemium default.
      setIsLoading(false);
      setErrorMessage(
        'We are experiencing an issue with payments. Please contact customer support: rentalsupport@zillow.com',
      );
    }
  }, []);

  useEffect(() => {
    setIsLoading(false);
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType: PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM,
      }),
    );
  }, [freemiumDetailsData]);

  useEffect(() => {
    setIsLoading(false);
    if (freemiumError?.message) {
      setErrorMessage(freemiumError.message);
    }
  }, [freemiumError]);

  useEffect(() => {
    if (shouldHandleValidPayment) {
      triggerPaymentSuccessCallback();
    }
  }, [isPaymentValid, isActiveProductExpired]);

  const triggerPaymentSuccessCallback = () => {
    try {
      // @ts-expect-error - window.ZRM_NATIVE_API is injected
      window.ZRM_NATIVE_API.onPaidInclusionPaymentMade('success');
    } catch (error) {
      logError({
        error,
        errorType: 'ZrmNativeApiError',
        context: 'This function is intended to be triggered inside a WebView. Check context.userAgent',
      });
    }
  };

  const handleFreemiumChargeSuccess = (response: FreemiumChargeResponseWebDto) => {
    if (activate === 'false') {
      if (response.isPaymentValid) {
        triggerPaymentSuccessCallback();
      }

      return;
    }

    if (response.isPaymentValid) {
      activateListing(response);
    }
  };

  const activateListing = (freemiumChargeResponse: FreemiumChargeResponseWebDto) => {
    return propertyActivate(
      { getParams: { listingAlias } },
      {
        onSuccess: (activateResponse) => {
          // sync what is available without having to do extra product lookups
          // expirationTimestamp is critical to update
          dispatch(
            PropertiesActions.updateCurrentProperty({
              daysExtended: activateResponse?.daysExtended,
              freemium: {
                ...freemium,
                activeProduct: {
                  ...freemium.activeProduct,
                  type: freemiumChargeResponse?.activeProductType,
                  expirationTimestamp: activateResponse?.expirationMs,
                },
              },
            }),
          );
        },
        onError: (error) => {
          handleActivationError(
            error as DatarouterError & {
              errorDetails: { data: { listingActivationFailed: boolean; activationError: { message: string } } };
            },
          );
        },
      },
    );
  };

  const handleActivationError = (
    error: DatarouterError & {
      errorDetails: { data: { listingActivationFailed: boolean; activationError: { message: string } } };
    },
  ) => {
    const { errorDetails, message: errorMessage } = error;
    const { listingActivationFailed, activationError } = errorDetails.data;

    // general message
    let message = errorMessage;

    if (listingActivationFailed) {
      if (activationError?.message) {
        // more specific message
        message = activationError?.message;
      }
    }

    setErrorMessage(message);
  };

  const onFreemiumChargeError = (error: DatarouterError) => {
    setErrorMessage(error.message);
  };

  const renderComponent = () => {
    if (errorMessage !== '') {
      return <Heading level={5}>{errorMessage}</Heading>;
    }

    if (shouldHandleValidPayment) {
      return <Heading level={5}>Payment is complete</Heading>;
    }

    return (
      <div>
        <Heading level={6} data-testid="MobilePaymentPage-Title">
          {headingText}
        </Heading>

        <PaymentDetails
          listingAlias={listingAlias}
          isRenderedInPageView={true}
          handleFreemiumChargeSuccess={handleFreemiumChargeSuccess}
          onFreemiumChargeError={onFreemiumChargeError}
          setHeadingText={setHeadingText}
        />
      </div>
    );
  };

  return (
    <Page.Root data-testid="MobilePaymentPage">
      <Page.Content>{isLoading ? <SpinnerContainer /> : renderComponent()}</Page.Content>
    </Page.Root>
  );
};

export default MobilePaymentPage;
